import { BigSwitch, ReactIcons } from "@kalyzee/kalyzee-ui";
import { useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useMemo, useRef, useState } from "react";
import { BannerButton } from "../../../../components/common/BannerButton";
import { CreateMeetingPopup } from "../../../../components/common/CreateMeetingPopup";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaConfigPopup } from "../../../../components/common/MediaConfigPopup";
import { MediaGlobalControls, MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useMeetingSources } from "../../../../hooks/meeting";
import { VodPlayerContentPage } from "../vod/player";
import { EventsAgenda } from "./EventsAgenda";
import styles from "./list.module.css";
import { VodsAgenda } from "./VodsAgenda";

type AgendaView = "events" | "vods";

const SWITCH_OPTIONS: { value: AgendaView; label: string }[] = [
  { value: "events", label: "Activités" },
  { value: "vods", label: "Replays" },
];

export const MeetingListStudentPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const sources = useMeetingSources();

  const isMobile = useAppStoreSelector(state => state.isMobile);

  const logoContainerRef = useRef<HTMLImageElement>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [mediaConfigOpen, setMediaConfigOpen] = useState(false);

  const initialView = useMemo<AgendaView>(() => {
    const v = pageContextRef.current?.searchParams?.get("view");
    return v === "vods" ? "vods" : "events";
  }, [pageContextRef]);
  const [view, setView] = useState<AgendaView>(initialView);

  const vodId = pageContextRef.current?.searchParams?.get("vodId") ?? undefined;

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup());

  const onChangeView = useCallback((value: AgendaView) => {
    setView(value);
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      { replace: true, queries: { view: value === "vods" ? "vods" : undefined, vodId: undefined } },
    );
  }, [pageContextRef]);

  const onCloseVod = useCallback(() => {
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      { replace: true, queries: { vodId: undefined } },
    );
  }, [pageContextRef]);

  return (
    <MasterUI onBack={vodId ? onCloseVod : undefined}>
      {vodId && (
        <VodPlayerContentPage
          id={vodId}
          style={{ position: "absolute", top: 0, left: 0, zIndex: 10, width: "100%", height: "100%", background: "black" }}
        />
      )}
      <AnimatedBackground contentClassName={styles.backgroundHome}>
        <div className={styles.mainHomeLogged}>
          <div className={styles.mainHomeLoggedContent}>
            <div className={styles.layout}>
              {!isMobile && (
                <div className={styles.mediaColumn}>
                  <MediaSourcePicker
                    sources={sources}
                    className={styles.mediaSelector}
                    // We can ignore gesture errors
                    // here because this component is only used to configure the sources
                    // it will be mounted again when joining the meeting
                    // and there wont be gesture errors anymore
                    ignoreGestureError
                  />
                  <MediaGlobalControls sources={sources} className={styles.globalControls} />
                </div>
              )}

              <div className={styles.switchColumn}>
                <BigSwitch
                  className={styles.bigSwitch}
                  options={SWITCH_OPTIONS}
                  value={view}
                  onChange={onChangeView}
                />
              </div>

              <div className={styles.eventsColumn}>
                {view === "events" ? <EventsAgenda /> : <VodsAgenda />}
              </div>

              <div className={styles.actionButtons}>
                {isMobile && (
                  <BannerButton
                    icon={<ReactIcons.Settings />}
                    title="Configurer mes médias"
                    subtitle="Choisissez caméra et micro avant de rejoindre"
                    background="linear-gradient(135deg, var(--color-meet-purple-2) 0%, var(--color-meet-purple-1) 100%)"
                    onClick={() => setMediaConfigOpen(true)}
                  />
                )}
                <BannerButton
                  icon={<ReactIcons.Camera />}
                  title="Créer un meeting instantané"
                  subtitle="Démarrez un nouveau cours en quelques secondes"
                  background="linear-gradient(135deg, var(--color-element-calendar-item-join-bg) 0%, var(--color-element-calendar-item-status-selected, var(--color-element-calendar-item-join-bg)) 100%)"
                  onClick={() => setCreateOpen(true)}
                />
              </div>
            </div>

            <CreateMeetingPopup
              visible={createOpen}
              onClose={() => setCreateOpen(false)}
              onCreated={(meeting) => {
                setCreateOpen(false);
                // Pass `openShare` via react-router state so the meeting
                // page knows to auto-open the share popup on arrival.
                pageContextRef.current?.goTo(
                  PageMasterMode.MEETING_DASHBOARD,
                  { id: meeting._id },
                  { state: { openShare: true } },
                );
              }}
            />
            <MediaConfigPopup
              visible={mediaConfigOpen}
              onClose={() => setMediaConfigOpen(false)}
              sources={sources}
            />
          </div>
        </div>
      </AnimatedBackground>
    </MasterUI>
  );
};

/* ------------------------------------------------------------------ */
/* Kept aside per request: "Voir les replays" banner button used to   */
/* live below the agenda. The BigSwitch above the agenda now toggles  */
/* between events and replays, but the banner is preserved here for   */
/* potential reuse.                                                   */
/*                                                                    */
/* <BannerButton                                                      */
/*   icon={<ReactIcons.PlayScreens />}                                */
/*   title="Voir les replays"                                         */
/*   subtitle="Retrouvez vos cours enregistrés"                       */
/*   background="linear-gradient(135deg,                              */
/*     var(--color-element-calendar-item-join-vod-bg) 0%,             */
/*     var(--color-element-calendar-item-join-vod-bg) 100%)"          */
/*   onClick={() => pageContextRef.current?.goTo(                     */
/*     PageMasterMode.LIST,                                           */
/*     undefined,                                                     */
/*     { queries: { view: "vods" } },                                 */
/*   )}                                                               */
/* />                                                                 */
/* ------------------------------------------------------------------ */
